import React from 'react'
import Card from '../shared/Card'
import Button from '../shared/Button'
import PaginationFooter from '../shared/PaginationFooter'
import { usePagination } from '../../hooks/usePagination'
import { PAGINATION } from '../../utils/dashboardConstants'
import { APP_CONFIG } from '../../utils/constants'

/**
 * Follow-on Opportunities component for displaying available follow-on deals
 * 
 * @param {Array} followOnOpportunities - Array of opportunity objects
 * @param {boolean} loading - Loading state
 */
const FollowOnOpportunities = ({ followOnOpportunities, loading }) => {
  const { currentPage, totalPages, currentPageData, setCurrentPage } = usePagination(
    followOnOpportunities, 
    PAGINATION.FOLLOW_ON_PAGE_SIZE
  )

  return (
    <section>
      <Card 
        className="p-0 overflow-hidden" 
        shadow="small"
        title="Follow-on Deals"
        subtitle="Recent Follow-on Opportunities"
      >
        <div className="divide-y divide-gray-200">
          {/* Render loading state or data */}
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading follow-on opportunities...</div>
          ) : followOnOpportunities.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No follow-on opportunities available</div>
          ) : currentPageData.map((opportunity, index) => (
            <div key={`${opportunity.company}-${index}`} className="p-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                <div className="flex-1 md:pr-3">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 text-base">
                      {opportunity.company}
                    </h3>
                    <p className={`px-2 py-1 ${opportunity.statusColor} text-white text-xs rounded-full`}>
                      {opportunity.status}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">
                      Deal Size: <span className="font-medium text-gray-900">{opportunity.dealSize}</span>
                    </p>
                  </div>
                </div>
                <div className="md:text-right md:ml-4 mt-3 md:mt-0">
                  {/* Prefer declarative navigation: internal route uses `to`; otherwise use `href` with fallback */}
                  <Button
                    variant="outline"
                    size="small"
                    className="text-sm"
                    {...(opportunity.startupId
                      ? { to: `/portfolio/${opportunity.startupId}` }
                      : {
                          href: (`mailto:${opportunity.primaryContactEmail || APP_CONFIG.SUPPORT_EMAIL}?subject=Follow-on Opportunity: ${opportunity.company}`),
                          target: '_blank',
                        }
                    )}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination footer for follow-on opportunities */}
        {followOnOpportunities.length > PAGINATION.FOLLOW_ON_PAGE_SIZE && (
          <PaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>
    </section>
  )
}

export default FollowOnOpportunities
